import { captureEvent } from "../../../../utils/o11y";
import { isNullish } from "remeda";
import { useState } from "react";
import { v4 } from "uuid";

import { useActivePageId } from "../../../../data";
import type { TRangeSelection } from "../../useRangeSelectionChange";
import { getCellValueFromRef, isCellRef } from "../../cell-ref";
import { rangeLabel, SelectionInfo } from "../SelectionInfo";
import { useGrid } from "../../stores";
import { HelperInfoIcon } from "../../../icons";
import { Tooltip, TooltipTrigger } from "@artis/ui";
import { AddRowButton, ManageTable } from "../Shared";
import { AlertRow } from "./AlertRow";
import { useOnSave } from "../ModalContainer";
import { useUpsertAlerts } from "../../../../data/alerts";
import type { TAlert, TAlertOptions } from "../../../../data/alerts/queries";
import { useUserId } from "../../../../context/auth/hooks";
import { calculateExpiredAt } from "../../../../data/alerts/expiration";

export const AddAlerts = ({ selection }: { selection: TRangeSelection }) => {
  "use no memo"; // FIXME: REACT-COMPILER - Error: This value cannot be modified

  const [alerts, setAlert] = useState<(TAlertOptions & { changed: boolean })[]>([
    { limit: "", status: "Active", value_below_limit: false, changed: false },
  ]);
  const pageId = useActivePageId();
  const userId = useUserId();
  const upsert = useUpsertAlerts();
  const [gridApi] = useGrid();

  useOnSave(async () => {
    const next: TAlert[] = [];
    for (const cell of selection.flat()) {
      for (const alert of alerts) {
        const { changed, ...rest } = alert;

        if (rest.limit?.trim() === "" || isNullish(rest.limit)) {
          throw new Error("All alerts must have a limit");
        }

        if (!changed) continue;
        const rowNode = gridApi?.getRowNode(cell.rowId);
        if (!rowNode) throw new Error("Row node not found");
        const value = gridApi?.getCellValue({ colKey: cell.columnId, rowNode });
        const limit = isCellRef(rest.limit) ? getCellValueFromRef(gridApi, rest.limit) : Number(rest.limit);

        // For adhoc rows (spreads), use the full spread identifier from rowNode.id
        // For regular rows, use the period
        const periodId = rowNode.data?.rowType === "adhoc" ? rowNode.data.id : cell.period;

        const createdAt = new Date().toISOString();
        // A draft row's expired_at is only set once the user picks a fixed date (Today/1
        // week/1 month/Custom); "None" leaves it unset so it resolves per-cell here, since a
        // single draft row can apply to several cells/periods with different auto expirations.
        const expiredAt = rest.expired_at ?? calculateExpiredAt(periodId, rest.limit);

        const newAlert: TAlert = {
          ...rest,
          id: v4(),
          product_id: cell.productId ?? "",
          column_id: cell.columnId,
          row_id: periodId, // Store full spread identifier for adhoc rows
          page_id: pageId,
          user_id: userId,
          created_at: createdAt,
          expired_at: expiredAt,
          value_below_limit: value < limit,
        };
        next.push(newAlert);
        captureEvent("created_alert", {
          pageId,
          productId: cell.productId as string,
          columnId: cell.columnId,
          rowId: periodId, // Log full spread identifier
        });
      }
    }
    if (next.length === 0) return;
    await upsert.mutateAsync(next);
  });

  const onAdd = () =>
    setAlert((y) => [...y, { limit: "", status: "Active", value_below_limit: false, changed: false }]);

  return (
    <>
      <SelectionInfo>{rangeLabel(selection ?? [])}</SelectionInfo>
      <ManageTable className="[grid-template:auto/auto_auto_auto_5rem_auto_auto_4.5rem]">
        <ManageTable.Header>
          <div className="flex items-center">Limit</div>
          <div className="flex items-center">Note</div>
          <div className="flex items-center gap-[2px]">
            Expiration
            <TooltipTrigger placement="top" touch="toggle" focusable>
              <span className="group inline-flex size-[1rem] shrink-0 cursor-default items-center justify-center overflow-visible leading-none rounded-full transition-colors hover:bg-neutral-tonal">
                <HelperInfoIcon />
              </span>
              <Tooltip>Alerts will expire when the selected cell or reference cell's contract expires</Tooltip>
            </TooltipTrigger>
          </div>
          <div className="flex items-center">Status</div>
          <div className="flex items-center justify-center">Recurring</div>
          <div className="flex items-center justify-center">Sound</div>
          <div />
        </ManageTable.Header>
        {alerts.map((alert, i) => (
          <AlertRow
            key={i.toString()}
            pageId={pageId}
            value={alert}
            noteWidth={183}
            onUpdate={(updates) =>
              setAlert((alerts) =>
                alerts.map((alert, i2) => (i2 === i ? { ...alert, ...updates, changed: true } : alert)),
              )
            }
            onRemove={alerts.length > 1 ? () => setAlert((y) => y.filter((_, i2) => i2 !== i)) : undefined}
          />
        ))}
      </ManageTable>
      <div className="flex items-center">
        <AddRowButton onClick={onAdd} />
      </div>
    </>
  );
};
